import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Timer,
  TrendingDown,
  TrendingUp,
  Users,
  Activity,
  Trophy,
  Building2,
  Contact,
  Calendar,
  Clock,
  AlertCircle,
  Lock,
  CheckCircle,
} from 'lucide-react';
import auctionViewerService, { AUCTION_ENDED_MESSAGE, SharedLiveAuction } from '../../../services/auctionViewerService';
import { rankLabel } from '../../../utils/auctionDirection';
import { formatAmount, formatAuctionDate, formatAuctionTime, formatBidClock } from './sharedAuctionFormat';
import './SharedAuctions.css';

const POLL_MS = 5000;

const pad = (n: number) => n.toString().padStart(2, '0');

const SharedAuctionLiveView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [auction, setAuction] = useState<SharedLiveAuction | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [bidFlash, setBidFlash] = useState(false);
  const prevBidCount = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await auctionViewerService.getSharedLive(id);
      setAuction(data);
      setSecondsLeft(data.seconds_remaining);
      setLastUpdated(new Date());
      setError(null);

      // Brief highlight when a new bid lands, so a glance at the screen shows it.
      if (prevBidCount.current !== null && data.statistics.total_bids > prevBidCount.current) {
        setBidFlash(true);
        window.setTimeout(() => setBidFlash(false), 2500);
      }
      prevBidCount.current = data.statistics.total_bids;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not load this auction';
      if (message === AUCTION_ENDED_MESSAGE) {
        // The auction finished (possibly while this page was open): drop the
        // stale live data so the ended screen below replaces it.
        setAuction(null);
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    prevBidCount.current = null;
    setLoading(true);
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Local 1s tick between polls; every poll re-syncs it from the server.
  const isLive = auction?.status === 'live';
  useEffect(() => {
    if (!isLive) return;
    const tick = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(tick);
  }, [isLive]);

  if (loading && !auction) {
    return (
      <div className="sv-page">
        <div className="sv-state">
          <div className="sv-spinner" />
          <p>Loading live auction...</p>
        </div>
      </div>
    );
  }

  if (error && !auction) {
    return (
      <div className="sv-page">
        <div className="sv-state sv-state-error">
          <AlertCircle className="w-6 h-6" />
          <h3>{error === AUCTION_ENDED_MESSAGE ? 'Auction ended' : "Can't open this auction"}</h3>
          <p>{error}</p>
          <button type="button" className="sv-btn sv-btn-secondary" onClick={() => navigate('/dashboard/shared')}>
            <ArrowLeft className="w-4 h-4" />
            Back to Shared Auctions
          </button>
        </div>
      </div>
    );
  }

  if (!auction) return null;

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const seconds = secondsLeft % 60;
  const timeUp = isLive && secondsLeft === 0;
  const isForward = auction.auction_mode === 'forward';
  const best = auction.statistics.best_bid ?? auction.statistics.lowest_bid;

  return (
    <div className="sv-page">
      <div className="sv-live-header">
        <button type="button" className="sv-back" onClick={() => navigate('/dashboard/shared')}>
          <ArrowLeft className="w-4 h-4" />
          Shared Auctions
        </button>

        <div className="sv-live-heading">
          <div className="sv-live-heading-top">
            <span className="sv-card-no">{auction.auction_no}</span>
            <span className={`sv-status sv-status-${auction.status}`}>
              {isLive && <span className="sv-live-dot" />}
              {auction.status.toUpperCase()}
            </span>
            <span className="sv-viewonly">
              <Eye className="w-3.5 h-3.5" />
              View only
            </span>
          </div>
          <h1 className="sv-live-title">{auction.title}</h1>
          <div className="sv-card-meta">
            <span>
              <Calendar className="w-4 h-4" />
              {formatAuctionDate(auction.auction_date)}
            </span>
            <span>
              <Clock className="w-4 h-4" />
              {formatAuctionTime(auction.start_time)}
              {auction.end_time ? ` – ${formatAuctionTime(auction.end_time)}` : ''}
            </span>
            {auction.managed_by && (
              <span>
                <Building2 className="w-4 h-4" />
                Managed by {auction.managed_by.company_name}
              </span>
            )}
          </div>
          {auction.description && <p className="sv-live-desc">{auction.description}</p>}
        </div>
      </div>

      <div className="sv-stats">
        <div className={`sv-stat sv-stat-primary ${bidFlash ? 'sv-flash' : ''}`}>
          <span className="sv-stat-label">
            {isForward ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {isForward ? 'Current highest bid (H1)' : 'Current lowest bid (L1)'}
          </span>
          <span className="sv-stat-value">{best !== null ? formatAmount(auction.currency, best) : 'No bids yet'}</span>
        </div>
        <div className="sv-stat">
          <span className="sv-stat-label">
            <Users className="w-4 h-4" />
            Bidders
          </span>
          <span className="sv-stat-value">
            {auction.statistics.bidders}
            <small> of {auction.statistics.total_participants} invited</small>
          </span>
        </div>
        <div className={`sv-stat ${bidFlash ? 'sv-flash' : ''}`}>
          <span className="sv-stat-label">
            <Activity className="w-4 h-4" />
            Total bids
          </span>
          <span className="sv-stat-value">{auction.statistics.total_bids}</span>
        </div>
      </div>

      <div className={`sv-countdown sv-countdown-${auction.status}`}>
        <div className="sv-countdown-label">
          <Timer className="w-5 h-5" />
          {auction.status === 'live'
            ? timeUp
              ? 'Time is up — finalising the result'
              : 'Auction closes in'
            : auction.status === 'upcoming'
            ? 'Auction starts'
            : auction.status === 'completed'
            ? 'Auction closed'
            : `Auction ${auction.status}`}
        </div>

        {auction.status === 'live' ? (
          <div className="sv-clock">
            <div>
              <span>{pad(hours)}</span>
              <small>Hours</small>
            </div>
            <b>:</b>
            <div>
              <span>{pad(minutes)}</span>
              <small>Min</small>
            </div>
            <b>:</b>
            <div>
              <span>{pad(seconds)}</span>
              <small>Sec</small>
            </div>
          </div>
        ) : auction.status === 'upcoming' ? (
          <div className="sv-clock-text">
            {formatAuctionDate(auction.auction_date)} at {formatAuctionTime(auction.start_time)}
          </div>
        ) : null}

        {isLive && (
          <p className="sv-countdown-note">Time may be extended automatically when a bid arrives in the last 3 minutes.</p>
        )}
      </div>

      {auction.status === 'completed' && auction.winner && (
        <div className="sv-winner">
          <Trophy className="w-6 h-6" />
          <div>
            <span>Winning bid</span>
            <strong>
              {auction.winner.company_name || auction.winner.person_name || 'Winner'} &middot;{' '}
              {formatAmount(auction.currency, auction.winner.amount)}
            </strong>
          </div>
        </div>
      )}

      <div className="sv-board">
        <div className="sv-board-header">
          <h3>
            <Trophy className="w-5 h-5" />
            Live Bids
          </h3>
          <span className="sv-board-sub">{isForward ? 'Highest price ranks first' : 'Lowest price ranks first'}</span>
        </div>

        {auction.leaderboard.length === 0 ? (
          <div className="sv-state sv-state-inline">
            <Users className="w-7 h-7" />
            <h4>No bids yet</h4>
            <p>{isLive ? 'Waiting for participants to place their first bid...' : 'No bids were placed.'}</p>
          </div>
        ) : (
          <ul className="sv-board-list">
            {auction.leaderboard.map((row) => (
              <li key={`${row.rank}-${row.amount}`} className={`sv-row ${row.rank === 1 ? 'sv-row-lead' : ''}`}>
                <span className={`sv-rank sv-rank-${Math.min(row.rank, 4)}`}>{rankLabel(auction.auction_mode, row.rank)}</span>

                <div className="sv-row-who">
                  <span className="sv-row-company">
                    <Building2 className="w-4 h-4" />
                    <span className="sv-name">{row.company_name || 'Unknown company'}</span>
                  </span>
                  <span className="sv-row-person">
                    <Contact className="w-4 h-4" />
                    <span className="sv-name">{row.person_name || 'Unknown'}</span>
                  </span>
                </div>

                <div className="sv-row-amount">
                  <strong>{formatAmount(auction.currency, row.amount)}</strong>
                  <small>
                    {formatBidClock(row.bid_time)}
                    {row.total_bids > 1 ? ` · ${row.total_bids} bids` : ''}
                  </small>
                </div>

                <span className={`sv-row-tag ${row.rank === 1 ? 'sv-row-tag-lead' : ''}`}>
                  {row.rank === 1 ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Leading
                    </>
                  ) : (
                    'Active'
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sv-live-foot">
        <p className="sv-footnote">
          <Lock className="w-3.5 h-3.5" />
          View-only access. Bidding, auction controls and reports are available only to the managing team.
        </p>
        {lastUpdated && (
          <p className="sv-updated">
            <span className={isLive ? 'sv-live-dot' : 'sv-idle-dot'} />
            {isLive ? 'Auto-refreshing' : 'Last updated'} · {lastUpdated.toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default SharedAuctionLiveView;
